import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface PrelaunchGateProps {
  children: React.ReactNode;
}

export function PrelaunchGate({ children }: PrelaunchGateProps) {
  const { user, loading, emailVerified } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }

    // Check if onboarding is complete (branding record exists)
    const checkAccess = async () => {
      const { data: branding } = await supabase
        .from('photographer_branding')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!branding) {
        // Onboarding not complete — send them back
        navigate('/onboarding', { replace: true });
        return;
      }

      // Onboarding done — now check email verification for non-Google users
      const isGoogleUser = user.app_metadata?.provider === 'google';

      // Client-side OTP check: magic link sessions are pre-verified. This prevents a
      // flash to /verify-email when the background verify-otp-session call in
      // Onboarding.tsx hasn't resolved before the user navigates here.
      let isOtpSession = false;
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (accessToken) {
        try {
          const [, payloadB64] = accessToken.split('.');
          const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
          isOtpSession = (payload?.amr ?? []).some((e: { method: string }) => e.method === 'otp');
        } catch { /* malformed JWT — treat as non-OTP */ }
      }

      if (!isGoogleUser && !emailVerified && !isOtpSession) {
        navigate('/verify-email', { replace: true });
        return;
      }

      // All checks passed
      setChecked(true);
    };

    checkAccess();
  }, [user, loading, emailVerified, navigate]);

  if (loading || !checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <>{children}</>;
}
